import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { Copy, ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { RelativeTime } from './relative-time';
import { copyToClipboard } from '@/lib/format';
import { useIsMobile } from '@/hooks/use-media-query';
import { useTranslation } from 'react-i18next';
import type { AuditEvent } from '@/schemas/audit';

const EVENT_BADGE_CLASS: Record<string, string> = {
  created: 'bg-status-active/10 text-status-active',
  activated: 'bg-status-active/10 text-status-active',
  revoked: 'bg-status-revoked/10 text-status-revoked',
  deactivated: 'bg-status-expired/10 text-status-expired',
  updated: 'bg-status-pending/10 text-status-pending',
};

const EVENT_LABEL_KEYS: Record<string, string> = {
  created: 'common.events.created',
  activated: 'common.events.activated',
  revoked: 'common.events.revoked',
  deactivated: 'common.events.deactivated',
  updated: 'common.events.updated',
};

export type RowClickHandler = (row: AuditEvent) => void;

function SortIndicator({ sorted }: { sorted: false | 'asc' | 'desc' }) {
  if (sorted === 'asc')
    return <ArrowUp className="h-3.5 w-3.5 text-foreground" aria-hidden="true" />;
  if (sorted === 'desc')
    return <ArrowDown className="h-3.5 w-3.5 text-foreground" aria-hidden="true" />;
  return <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" aria-hidden="true" />;
}

interface Props {
  data: AuditEvent[];
  sorting: SortingState;
  onSortingChange: (u: SortingState) => void;
  onRowClick: RowClickHandler;
}

export function EventVerb({ event, label }: { event: string; label: string }) {
  const badgeClass = EVENT_BADGE_CLASS[event] ?? 'bg-muted text-muted-foreground';
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-md px-2 py-0.5 text-[12.5px] font-medium ${badgeClass}`}
    >
      {label}
    </span>
  );
}

export function AuditTable({ data, sorting, onSortingChange, onRowClick }: Props) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const columns = useMemo<ColumnDef<AuditEvent>[]>(() => {
    const eventEl = (r: AuditEvent) => {
      const labelKey = EVENT_LABEL_KEYS[r.event];
      const ev = labelKey ? t(labelKey) : r.event;
      return <EventVerb event={r.event} label={ev} />;
    };

    return [
      {
        accessorKey: 'createdAt',
        header: t('audit.table.when'),
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-[13px] text-muted-foreground">
            <RelativeTime date={row.original.createdAt} />
          </span>
        ),
      },
      {
        accessorKey: 'event',
        header: t('audit.table.event'),
        cell: ({ row }) => eventEl(row.original),
      },
      {
        accessorKey: 'serial',
        header: t('audit.table.serial'),
        cell: ({ row }) => {
          const s = row.original.serial;
          return (
            <div className="group flex items-center gap-2">
              {row.original.licenseId ? (
                <Link
                  to={`/licenses/${row.original.licenseId}`}
                  className="max-w-[220px] truncate rounded-sm font-mono text-[13.5px] font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={(e) => e.stopPropagation()}
                >
                  {s}
                </Link>
              ) : (
                <span className="max-w-[220px] truncate font-mono text-[13.5px] font-medium text-foreground">
                  {s}
                </span>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  void copyToClipboard(s).then(() => {
                    toast.success(t('common.copied'));
                  });
                }}
                className="opacity-40 transition-opacity hover:opacity-100 focus:opacity-100 group-hover/row:opacity-100 group-hover:opacity-100"
                aria-label={t('licenses.table.copySerialAria', { serial: s })}
                title={t('common.copySerial')}
              >
                <Copy className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </div>
          );
        },
      },
      {
        accessorKey: 'performedBy',
        header: t('audit.table.by'),
        cell: ({ row }) => {
          const by = row.original.performedBy;
          if (by === 'api') return <span className="font-mono text-[13px] text-faint">api</span>;
          return <span className="text-[13.5px] text-muted-foreground">{by}</span>;
        },
      },
      {
        accessorKey: 'ipAddress',
        header: t('audit.table.from'),
        cell: ({ row }) => {
          const ip = row.original.ipAddress ?? '-';
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="max-w-[140px] truncate font-mono text-xs text-muted-foreground">
                    {ip}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <span className="font-mono text-xs">{ip}</span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
      },
    ];
  }, [t]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: (u) => onSortingChange(typeof u === 'function' ? u(sorting) : u),
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
  });

  if (isMobile) {
    return (
      <ul className="border-t">
        {data.map((event) => {
          const labelKey = EVENT_LABEL_KEYS[event.event];
          const label = labelKey ? t(labelKey) : event.event;
          return (
            <li key={event.id} className="border-b">
              <button
                className="flex w-full items-baseline justify-between gap-3 px-1 py-3.5 text-left transition-colors hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => onRowClick(event)}
              >
                <span className="min-w-0">
                  <EventVerb event={event.event} label={label} />
                  <span className="mt-1 block truncate font-mono text-[13px] text-muted-foreground">
                    {event.serial}
                  </span>
                </span>
                <span className="shrink-0 whitespace-nowrap text-[13px] text-faint">
                  <RelativeTime date={event.createdAt} />
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <Table className="min-w-[720px] md:min-w-0">
      <TableHeader className="sticky top-0 z-10 bg-background">
        {table.getHeaderGroups().map((hg) => (
          <TableRow key={hg.id} className="bg-foreground/[0.03] hover:bg-foreground/[0.03]">
            {hg.headers.map((h) => (
              <TableHead key={h.id} className="h-11 px-3 text-[13px] font-medium text-faint">
                {h.column.getCanSort() ? (
                  <button
                    className="flex items-center gap-1.5 rounded-sm font-medium transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={h.column.getToggleSortingHandler()}
                    aria-sort={
                      h.column.getIsSorted() === 'asc'
                        ? 'ascending'
                        : h.column.getIsSorted() === 'desc'
                          ? 'descending'
                          : 'none'
                    }
                  >
                    {flexRender(h.column.columnDef.header, h.getContext())}
                    <SortIndicator sorted={h.column.getIsSorted()} />
                  </button>
                ) : (
                  flexRender(h.column.columnDef.header, h.getContext())
                )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => (
          <TableRow
            key={row.id}
            tabIndex={0}
            className="group/row cursor-pointer hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => onRowClick(row.original)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onRowClick(row.original);
            }}
          >
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id} className="px-3 py-3">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
